import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { supabase } from '@/lib/supabase';
import { ensureVolunteerProfile } from '@/utils/ensureUserProfile';
import {
  clearVolunteerOAuthSignupIntent,
  consumeOAuthDuplicateUiPending,
  getVolunteerOAuthStoredNext,
  peekVolunteerOAuthSignupIntent,
} from '@/constants/volunteerOAuthSignup';
import { isDuplicateEmailError } from '@/constants/signupErrors';
import { DuplicateEmailHelpLinks } from '@/components/auth/DuplicateEmailHelpLinks';
import { logger } from '@/utils/logger';
import { consumePostAuthRedirect } from '@/utils/postAuthRedirect';

/**
 * OAuth return URL: exchange auth code for session, ensure profile exists, then redirect.
 */
export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const completeAuth = async () => {
      try {
        if (consumeOAuthDuplicateUiPending()) {
          if (!cancelled) {
            setDuplicateEmail(true);
            setError(null);
          }
          return;
        }

        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const queryError =
          searchParams.get('error_description') ||
          searchParams.get('error') ||
          hashParams.get('error_description') ||
          hashParams.get('error');

        if (queryError) {
          const lower = queryError.toLowerCase();
          if (
            lower.includes('already') ||
            lower.includes('registered') ||
            lower.includes('exists') ||
            lower.includes('identity')
          ) {
            await supabase.auth.signOut({ scope: 'local' });
            clearVolunteerOAuthSignupIntent();
            if (!cancelled) {
              setDuplicateEmail(true);
              setError(null);
            }
            return;
          }
          throw new Error(queryError);
        }

        // The OAuth redirect URL carries no query params (dropping them fixed a native
        // redirect-allowlist mismatch), so the signup intent rides on localStorage only.
        const volunteerOAuthSignup = peekVolunteerOAuthSignupIntent();
        const storedNext = volunteerOAuthSignup ? getVolunteerOAuthStoredNext() : null;

        // Supabase client (detectSessionInUrl) usually exchanges ?code before this effect runs.
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        if (!session?.user) {
          throw new Error('Sign-in did not complete. Please try again.');
        }

        // Same social identity signing in — whether via the login or the "sign up with
        // Google/Apple" button — logs in and returns the existing profile. A genuine
        // cross-identity email collision still throws DuplicateEmailError (handled below).
        const profile = await ensureVolunteerProfile(session.user);

        if (cancelled) return;

        clearVolunteerOAuthSignupIntent();

        const savedRedirect = consumePostAuthRedirect();
        const next = savedRedirect ?? searchParams.get('next') ?? storedNext;
        const safeNext =
          next && next.startsWith('/') && !next.startsWith('//') ? next : '/organizations';
        // A returning, already-onboarded user who used the "sign up" button shouldn't be
        // pushed back through onboarding.
        const destination =
          profile?.onboarding_completed === true && safeNext === '/onboarding'
            ? '/organizations'
            : safeNext;
        navigate(destination, { replace: true });
      } catch (err: unknown) {
        logger.error('AuthCallback: failed to complete OAuth', err);
        clearVolunteerOAuthSignupIntent();
        if (!cancelled) {
          if (isDuplicateEmailError(err)) {
            setDuplicateEmail(true);
            setError(null);
          } else {
            const message =
              err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
            setError(message);
            setDuplicateEmail(false);
          }
        }
      }
    };

    void completeAuth();

    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <Flex
      minH="100vh"
      bgGradient="to-b"
      gradientFrom="gray.50"
      gradientTo="white"
      justify="center"
      align="center"
      p="5"
      direction="column"
      gap="3"
    >
      {duplicateEmail ? (
        <Box w="full" maxW="md" textAlign="center">
          <Text color="volyx.error" fontSize="sm" mb="1">
            An account with this email address already exists.
          </Text>
          <DuplicateEmailHelpLinks />
        </Box>
      ) : error ? (
        <>
          <Text color="volyx.error" textAlign="center" maxW="md">
            {error}
          </Text>
          <chakra.button
            type="button"
            bg="transparent"
            border="none"
            color="volyx.primary"
            fontWeight="semibold"
            cursor="pointer"
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to login
          </chakra.button>
        </>
      ) : (
        <Text fontSize="lg" fontWeight="medium" color="volyx.primary">
          Finishing sign-in…
        </Text>
      )}
    </Flex>
  );
};
